import { CSSProperties } from 'react';

import useSessionStore from 'hooks/stores/useSessionStore';

// hoogte / breedte; alleen gebruikt als de sessie geen productSize heeft
export const DEFAULT_PAGE_ASPECT_RATIO = Math.SQRT2;

// previewImageUrl/previewThumbnailUrl worden server-side gerenderd op trim+bleed-formaat met een
// bleed-marker erin gebakken (zie generateNewPreview.controller.js). Boekweergaven tonen alleen de
// trimbox: elke rand wordt afgesneden met zijn exacte bleed-fractie (uit session.productSize +
// session.bleed — gelijk voor elke side in een product) plus deze kleine veiligheidsmarge, die
// sub-pixel/anti-aliasing aan de bleed-rand opvangt zodat er nooit een sliver marker doorschijnt.
const BLEED_CROP_SAFETY_MARGIN = 0.004;

export type TrimboxCrop = {
  hasValidPageSize: boolean;
  // hoogte / breedte van de trimbox
  aspectRatio: number;
  insetLeftFraction: number;
  insetRightFraction: number;
  insetTopFraction: number;
  insetBottomFraction: number;
  // Hoeveel groter dan het zichtbare trimbox-vak de volledige bled-afbeelding gerenderd moet
  // worden, zodat na het afsnijden van de insets precies de trimbox overblijft.
  imageHorizontalScale: number;
  imageVerticalScale: number;
  // Style voor een <img> in een container met position:relative + overflow:hidden die de
  // afmetingen van de trimbox heeft: oversized en naar links/boven verschoven.
  imageStyle: CSSProperties;
};

const useTrimboxCrop = (): TrimboxCrop => {
  const { productSize, bleed } = useSessionStore((state) => ({
    productSize: state.session?.productSize,
    bleed: state.session?.bleed,
  }));

  const productWidthMm = Number(productSize?.width) || 0;
  const productHeightMm = Number(productSize?.height) || 0;
  const bleedLeftMm = bleed?.left ?? 0;
  const bleedRightMm = bleed?.right ?? 0;
  const bleedTopMm = bleed?.top ?? 0;
  const bleedBottomMm = bleed?.bottom ?? 0;
  const bledWidthMm = productWidthMm + bleedLeftMm + bleedRightMm;
  const bledHeightMm = productHeightMm + bleedTopMm + bleedBottomMm;
  const hasValidPageSize = productWidthMm > 0 && productHeightMm > 0;

  const aspectRatio = hasValidPageSize
    ? productHeightMm / productWidthMm
    : DEFAULT_PAGE_ASPECT_RATIO;

  const insetLeftFraction = hasValidPageSize
    ? bleedLeftMm / bledWidthMm + BLEED_CROP_SAFETY_MARGIN
    : 0;
  const insetRightFraction = hasValidPageSize
    ? bleedRightMm / bledWidthMm + BLEED_CROP_SAFETY_MARGIN
    : 0;
  const insetTopFraction = hasValidPageSize
    ? bleedTopMm / bledHeightMm + BLEED_CROP_SAFETY_MARGIN
    : 0;
  const insetBottomFraction = hasValidPageSize
    ? bleedBottomMm / bledHeightMm + BLEED_CROP_SAFETY_MARGIN
    : 0;
  const imageHorizontalScale = 1 / (1 - insetLeftFraction - insetRightFraction);
  const imageVerticalScale = 1 / (1 - insetTopFraction - insetBottomFraction);

  const imageStyle: CSSProperties = {
    position: 'absolute',
    width: `${imageHorizontalScale * 100}%`,
    height: `${imageVerticalScale * 100}%`,
    left: `${-insetLeftFraction * imageHorizontalScale * 100}%`,
    top: `${-insetTopFraction * imageVerticalScale * 100}%`,
    maxWidth: 'none',
    maxHeight: 'none',
  };

  return {
    hasValidPageSize,
    aspectRatio,
    insetLeftFraction,
    insetRightFraction,
    insetTopFraction,
    insetBottomFraction,
    imageHorizontalScale,
    imageVerticalScale,
    imageStyle,
  };
};

export default useTrimboxCrop;
